/**
 * Assessment form page — assessor fills HS, SS, and AKHLAK questionnaires
 */

import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useAuth } from '@/core/auth/AuthContext'
import { penilaianApi } from '../api/penilaianApi'
import type {
  HSResponseItem,
  SSResponseItem,
  AkhlakResponseItem,
  NoteItem,
  SubmitAssessmentInput,
  KPIAssignment,
} from '../types/penilaian.types'
import { SS_SCORE_LABELS, AKHLAK_SCORE_LABELS, HS_SCORE_LABELS } from '../types/penilaian.types'

type Tab = 'hs' | 'ss' | 'akhlak'

const ScoreButton = ({
  label,
  value,
  selected,
  onClick,
  color,
}: {
  label: string
  value: number
  selected: boolean
  onClick: () => void
  color: string
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
      selected
        ? `${color} border-current`
        : 'border-gray-200 text-gray-600 hover:border-gray-400 bg-white'
    }`}
  >
    {value} — {label}
  </button>
)

const SS_COLORS: Record<number, string> = {
  4: 'bg-green-100 text-green-700',
  3: 'bg-yellow-100 text-yellow-700',
  2: 'bg-orange-100 text-orange-700',
  1: 'bg-red-100 text-red-700',
}

const HS_COLORS: Record<number, string> = {
  5: 'bg-green-100 text-green-700',
  4: 'bg-blue-100 text-blue-700',
  3: 'bg-yellow-100 text-yellow-700',
  2: 'bg-orange-100 text-orange-700',
  1: 'bg-red-100 text-red-700',
}

function KpiCard({
  kpiAsgn,
  isCompany,
  realisasiDraft,
  setRealisasiDraft,
  realisasiPctDraft,
  setRealisasiPctDraft,
  hsScores,
  setHsScores,
  kpiNotes,
  setKpiNotes,
  isSubmitted,
  isClosed,
  isAdminHr,
  updateRealisasiMutation,
}: {
  kpiAsgn: KPIAssignment
  isCompany: boolean
  realisasiDraft: Record<string, string>
  setRealisasiDraft: (v: Record<string, string>) => void
  realisasiPctDraft: Record<string, string>
  setRealisasiPctDraft: (v: Record<string, string>) => void
  hsScores: Record<string, number>
  setHsScores: (v: Record<string, number>) => void
  kpiNotes: Record<string, string>
  setKpiNotes: (v: Record<string, string>) => void
  isSubmitted: boolean
  isClosed: boolean
  isAdminHr: boolean
  updateRealisasiMutation: { mutate: (v: { id: string; realisasi: string; realisasi_pct: number | null }) => void; isPending: boolean }
}) {
  const realisasiLocked = isCompany && !isAdminHr
  const canSaveRealisasi = isAdminHr ? true : (!isSubmitted && !isClosed && !realisasiLocked)

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="mb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-900">{kpiAsgn.kpi?.name}</h3>
            {kpiAsgn.kpi?.target_description && (
              <p className="text-sm text-gray-500 mt-0.5">{kpiAsgn.kpi.target_description}</p>
            )}
          </div>
          <span className="shrink-0 text-xs font-medium bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
            Bobot {kpiAsgn.bobot}%
          </span>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 mb-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Pencapaian KPI
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <p className="text-xs text-gray-500 mb-1">Target</p>
            <p className="text-sm font-medium text-gray-800 bg-white border border-gray-200 rounded-lg px-3 py-2">
              {kpiAsgn.target_value || kpiAsgn.kpi?.target_description || (
                <span className="text-gray-400 italic">Belum diisi</span>
              )}
            </p>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Realisasi</label>
            <input
              type="text"
              value={realisasiDraft[kpiAsgn.id] ?? ''}
              onChange={(e) => setRealisasiDraft({ ...realisasiDraft, [kpiAsgn.id]: e.target.value })}
              disabled={!canSaveRealisasi}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
              placeholder={realisasiLocked ? 'Diisi oleh admin' : 'Masukkan realisasi...'}
            />
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-xs text-gray-500 mb-1 block">% Pencapaian</label>
              <input
                type="number"
                value={realisasiPctDraft[kpiAsgn.id] ?? ''}
                onChange={(e) =>
                  setRealisasiPctDraft({ ...realisasiPctDraft, [kpiAsgn.id]: e.target.value })
                }
                disabled={!canSaveRealisasi}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="0"
                min={0}
                max={110}
              />
            </div>
            {canSaveRealisasi && (
              <button
                type="button"
                onClick={() =>
                  updateRealisasiMutation.mutate({
                    id: kpiAsgn.id,
                    realisasi: realisasiDraft[kpiAsgn.id] ?? '',
                    realisasi_pct: realisasiPctDraft[kpiAsgn.id]
                      ? parseFloat(realisasiPctDraft[kpiAsgn.id])
                      : null,
                  })
                }
                disabled={updateRealisasiMutation.isPending || !realisasiDraft[kpiAsgn.id]}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-40 whitespace-nowrap"
              >
                Simpan
              </button>
            )}
          </div>
        </div>
      </div>

      {(kpiAsgn.kpi?.hs_questions ?? []).length > 0 && (
        <div className="space-y-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Penilaian</p>
          {(kpiAsgn.kpi?.hs_questions ?? []).map((q, i) => (
            <div key={q.id} className="pb-4 border-b border-gray-100 last:border-0 last:pb-0">
              <p className="text-sm text-gray-700 mb-2">
                {i + 1}. {q.question_text}
              </p>
              <div className="flex flex-wrap gap-2">
                {[5, 4, 3, 2, 1].map((val) => (
                  <ScoreButton
                    key={val}
                    value={val}
                    label={HS_SCORE_LABELS[val]}
                    selected={hsScores[q.id] === val}
                    onClick={() => setHsScores({ ...hsScores, [q.id]: val })}
                    color={HS_COLORS[val]}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isCompany && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Komentar
          </label>
          <textarea
            value={kpiNotes[kpiAsgn.id] ?? ''}
            onChange={(e) => setKpiNotes({ ...kpiNotes, [kpiAsgn.id]: e.target.value })}
            disabled={isAdminHr ? false : (isSubmitted || isClosed)}
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none disabled:bg-gray-100 disabled:text-gray-500"
            placeholder="Tambahkan komentar untuk KPI ini..."
          />
        </div>
      )}
    </div>
  )
}

function KpiHardSkillTab({
  kpiAssignments,
  realisasiDraft,
  setRealisasiDraft,
  realisasiPctDraft,
  setRealisasiPctDraft,
  hsScores,
  setHsScores,
  kpiNotes,
  setKpiNotes,
  isSubmitted,
  isClosed,
  isAdminHr,
  updateRealisasiMutation,
}: {
  kpiAssignments: KPIAssignment[]
  realisasiDraft: Record<string, string>
  setRealisasiDraft: (v: Record<string, string>) => void
  realisasiPctDraft: Record<string, string>
  setRealisasiPctDraft: (v: Record<string, string>) => void
  hsScores: Record<string, number>
  setHsScores: (v: Record<string, number>) => void
  kpiNotes: Record<string, string>
  setKpiNotes: (v: Record<string, string>) => void
  isSubmitted: boolean
  isClosed: boolean
  isAdminHr: boolean
  updateRealisasiMutation: { mutate: (v: { id: string; realisasi: string; realisasi_pct: number | null }) => void; isPending: boolean }
}) {
  const companyKpis = kpiAssignments.filter((k) => k.kpi?.kpi_type === 'company')
  const individualKpis = kpiAssignments.filter((k) => k.kpi?.kpi_type === 'individual')

  const cardProps = {
    realisasiDraft,
    setRealisasiDraft,
    realisasiPctDraft,
    setRealisasiPctDraft,
    hsScores,
    setHsScores,
    kpiNotes,
    setKpiNotes,
    isSubmitted,
    isClosed,
    isAdminHr,
    updateRealisasiMutation,
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        Nilai kinerja berdasarkan KPI yang telah ditetapkan. Skala: 1 (Unsatisfactory) sampai 5
        (Excellent).
      </div>
      {kpiAssignments.length === 0 ? (
        <p className="text-gray-500 text-sm italic">
          KPI questionnaire akan ditampilkan setelah admin mengassign KPI kepada pegawai yang
          dinilai.
        </p>
      ) : (
        <>
          {companyKpis.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-3">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  KPI Perusahaan
                </h2>
                <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                  Realisasi diisi oleh admin
                </span>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-8">No</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Nama KPI</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">Bobot</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-40">Target</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-40">Realisasi</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">% Pencapaian</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {companyKpis.map((k, i) => (
                      <tr key={k.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{k.kpi?.name}</p>
                          {k.kpi?.target_description && (
                            <p className="text-xs text-gray-400 mt-0.5">{k.kpi.target_description}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                            {k.bobot}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">
                          {k.target_value || k.kpi?.target_description || (
                            <span className="text-gray-400 italic">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {k.realisasi ?? <span className="text-gray-400 italic">Belum diisi</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {k.realisasi_pct != null ? (
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              k.realisasi_pct >= 100
                                ? 'bg-green-50 text-green-700'
                                : k.realisasi_pct >= 75
                                ? 'bg-yellow-50 text-yellow-700'
                                : 'bg-red-50 text-red-700'
                            }`}>
                              {k.realisasi_pct}%
                            </span>
                          ) : (
                            <span className="text-gray-400 italic text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {individualKpis.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                KPI Individu
              </h2>
              {individualKpis.map((k) => (
                <KpiCard key={k.id} kpiAsgn={k} isCompany={false} {...cardProps} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default function AssessmentFormPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<Tab>('ss')

  const [hsScores, setHsScores] = useState<Record<string, number>>({})
  const [ssScores, setSsScores] = useState<Record<string, number>>({})
  const [akhlakScores, setAkhlakScores] = useState<Record<string, number>>({})
  const [akhlakNotes, setAkhlakNotes] = useState<Record<string, string>>({})
  const [ssNotes, setSsNotes] = useState<Record<string, string>>({})
  const [realisasiDraft, setRealisasiDraft] = useState<Record<string, string>>({})
  const [realisasiPctDraft, setRealisasiPctDraft] = useState<Record<string, string>>({})
  const [kpiNotes, setKpiNotes] = useState<Record<string, string>>({})
  const [confirmSubmit, setConfirmSubmit] = useState(false)

  const { data: assignment, isLoading: assignmentLoading } = useQuery({
    queryKey: ['assignment', assignmentId],
    queryFn: () => penilaianApi.getAssignment(assignmentId!),
    enabled: !!assignmentId,
  })

  const { data: savedResponses } = useQuery({
    queryKey: ['assignment-responses', assignmentId],
    queryFn: () => penilaianApi.getAssignmentResponses(assignmentId!),
    enabled: !!assignmentId,
  })

  const { data: kpiAssignments, isLoading: kpiLoading } = useQuery({
    queryKey: ['employee-kpis', assignment?.period_id, assignment?.assessee_id],
    queryFn: () =>
      penilaianApi.getEmployeeKPIAssignments(assignment!.period_id, assignment!.assessee_id),
    enabled: !!assignment?.period_id && !!assignment?.assessee_id,
  })

  const { data: ssIndicators, isLoading: ssLoading } = useQuery({
    queryKey: ['ss-indicators'],
    queryFn: () => penilaianApi.getSSIndicators(),
  })

  const { data: akhlakValues, isLoading: akhlakLoading } = useQuery({
    queryKey: ['akhlak-values'],
    queryFn: () => penilaianApi.getAkhlakValues(),
  })

  useEffect(() => {
    if (!savedResponses) return

    if (savedResponses.hs_responses.length > 0) {
      const loaded: Record<string, number> = {}
      for (const r of savedResponses.hs_responses) {
        loaded[r.hs_question_id] = r.score
      }
      setHsScores(loaded)
    }

    if (savedResponses.ss_responses.length > 0) {
      const loaded: Record<string, number> = {}
      for (const r of savedResponses.ss_responses) {
        loaded[r.ss_question_id] = r.score
      }
      setSsScores(loaded)
    }

    if (savedResponses.akhlak_responses.length > 0) {
      const loadedScores: Record<string, number> = {}
      const loadedNotes: Record<string, string> = {}
      for (const r of savedResponses.akhlak_responses) {
        loadedScores[r.behavior_id] = r.score
        if (r.tindakan_aktual) loadedNotes[r.behavior_id] = r.tindakan_aktual
      }
      setAkhlakScores(loadedScores)
      setAkhlakNotes(loadedNotes)
    }

    if (savedResponses.notes.length > 0) {
      const loadedSsNotes: Record<string, string> = {}
      const loadedKpiNotes: Record<string, string> = {}
      for (const n of savedResponses.notes) {
        if (n.note_type === 'ss' && n.note_text) {
          loadedSsNotes[n.reference_id] = n.note_text
        } else if (n.note_type === 'kpi' && n.note_text) {
          loadedKpiNotes[n.reference_id] = n.note_text
        }
      }
      setSsNotes(loadedSsNotes)
      setKpiNotes(loadedKpiNotes)
    }
  }, [savedResponses])

  useEffect(() => {
    if (!kpiAssignments) return
    const r: Record<string, string> = {}
    const rp: Record<string, string> = {}
    for (const a of kpiAssignments) {
      r[a.id] = a.realisasi ?? ''
      rp[a.id] = a.realisasi_pct != null ? String(a.realisasi_pct) : ''
    }
    setRealisasiDraft(r)
    setRealisasiPctDraft(rp)
  }, [kpiAssignments])

  const { user } = useAuth()
  const isAdminHr = (user?.roles ?? []).some((r: { name: string }) => r.name === 'admin' || r.name === 'hr')

  const periodStatus = assignment?.period?.status
  const isClosed = periodStatus === 'closed'
  const isDraft = periodStatus === 'draft'
  const isSubmitted = assignment?.status === 'submitted'
  const canSubmit = periodStatus === 'open' && !isSubmitted
  const canSaveDraft = !isClosed && !isSubmitted

  const allSsQuestions = (ssIndicators ?? [])
    .filter((ind) => ind.is_active)
    .flatMap((ind) => ind.questions.filter((q) => q.is_active))
  const allAkhlakBehaviors = (akhlakValues ?? [])
    .filter((v) => v.is_active)
    .flatMap((v) => v.behaviors.filter((b) => b.is_active))
  const ssMissing = allSsQuestions.filter((q) => ssScores[q.id] === undefined).length
  const akhlakMissing = allAkhlakBehaviors.filter((b) => akhlakScores[b.id] === undefined).length
  const ssComplete = allSsQuestions.length === 0 || ssMissing === 0
  const akhlakComplete = allAkhlakBehaviors.length === 0 || akhlakMissing === 0
  const canFinalSubmit = canSubmit && ssComplete && akhlakComplete

  const submitMutation = useMutation({
    mutationFn: (data: SubmitAssessmentInput) =>
      penilaianApi.submitAssessment(assignmentId!, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['assignment-responses', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['assignment', assignmentId] })
      if (variables.is_draft) {
        toast.success('Draft disimpan')
      } else if (isAdminHr) {
        toast.success('Penilaian berhasil dikumpulkan atas nama penilai')
        setConfirmSubmit(false)
      } else {
        toast.success('Penilaian berhasil dikumpulkan')
        navigate('/penilaian/my-assessments')
      }
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal menyimpan'),
  })

  // ── Live score preview (mirrors backend formula) ──────────────────────────

  const hsQuestionAvg = useMemo(() => {
    const all = (kpiAssignments ?? []).flatMap((k) =>
      (k.kpi?.hs_questions ?? []).flatMap((q) =>
        hsScores[q.id] !== undefined ? [hsScores[q.id]] : []
      )
    )
    return all.length > 0 ? all.reduce((a, b) => a + b, 0) / all.length : null
  }, [kpiAssignments, hsScores])

  const hsScore = useMemo((): number | null => {
    if (!kpiAssignments || kpiAssignments.length === 0) return null
    let total = 0, totalBobot = 0
    for (const k of kpiAssignments) {
      const bobotFraction = k.bobot / 100.0
      total += bobotFraction * (k.realisasi_pct ?? 0)
      totalBobot += bobotFraction
    }
    return totalBobot > 0 ? total / totalBobot : null
  }, [kpiAssignments])

  const SCORE_POINTS: Record<number, number> = { 4: 100, 3: 75, 2: 60, 1: 50 }

  const ssScore = useMemo((): number | null => {
    if (!ssIndicators) return null
    let total = 0, totalBobot = 0
    for (const ind of ssIndicators) {
      const pts = ind.questions
        .filter((q) => ssScores[q.id] !== undefined)
        .map((q) => SCORE_POINTS[ssScores[q.id]!] ?? 0)
      if (pts.length === 0) continue
      const avg = pts.reduce((a, b) => a + b, 0) / pts.length
      total += (ind.bobot as number) * avg
      totalBobot += ind.bobot as number
    }
    return totalBobot > 0 ? total / totalBobot : null
  }, [ssIndicators, ssScores])

  const akhlakScore = useMemo((): number | null => {
    if (!akhlakValues || akhlakValues.length === 0) return null
    const bobotEach = 1.0 / akhlakValues.length
    let total = 0, totalBobot = 0
    for (const val of akhlakValues) {
      const pts = val.behaviors
        .filter((b) => akhlakScores[b.id] !== undefined)
        .map((b) => SCORE_POINTS[akhlakScores[b.id]!] ?? 0)
      if (pts.length === 0) continue
      const avg = pts.reduce((a, b) => a + b, 0) / pts.length
      total += bobotEach * avg
      totalBobot += bobotEach
    }
    return totalBobot > 0 ? total / totalBobot : null
  }, [akhlakValues, akhlakScores])

const updateRealisasiMutation = useMutation({
    mutationFn: ({
      id,
      realisasi,
      realisasi_pct,
    }: {
      id: string
      realisasi: string
      realisasi_pct: number | null
    }) => penilaianApi.updateRealisasi(id, { realisasi, realisasi_pct }),
    onSuccess: () => {
      toast.success('Realisasi disimpan')
      queryClient.invalidateQueries({
        queryKey: ['employee-kpis', assignment?.period_id, assignment?.assessee_id],
      })
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal menyimpan realisasi'),
  })

  const buildPayload = (isDraft: boolean): SubmitAssessmentInput => {
    const questionToKpiAsgn: Record<string, string> = {}
    for (const kpiAsgn of kpiAssignments ?? []) {
      for (const q of kpiAsgn.kpi?.hs_questions ?? []) {
        questionToKpiAsgn[q.id] = kpiAsgn.id
      }
    }

    const hs_responses: HSResponseItem[] = Object.entries(hsScores).map(([qid, score]) => ({
      hs_question_id: qid,
      kpi_assignment_id: questionToKpiAsgn[qid] ?? '',
      score,
    }))

    const ss_responses: SSResponseItem[] = Object.entries(ssScores).map(([qid, score]) => ({
      ss_question_id: qid,
      score,
    }))

    const akhlak_responses: AkhlakResponseItem[] = Object.entries(akhlakScores).map(
      ([bid, score]) => ({
        behavior_id: bid,
        score,
        tindakan_aktual: akhlakNotes[bid] || null,
      })
    )

    const notes: NoteItem[] = [
      ...Object.entries(ssNotes)
        .filter(([, text]) => text)
        .map(([indicatorId, note_text]) => ({
          note_type: 'ss' as const,
          reference_id: indicatorId,
          note_text,
        })),
      ...Object.entries(kpiNotes)
        .filter(([, text]) => text)
        .map(([kpiAssignmentId, note_text]) => ({
          note_type: 'kpi' as const,
          reference_id: kpiAssignmentId,
          note_text,
        })),
    ]

    return { hs_responses, ss_responses, akhlak_responses, notes, is_draft: isDraft }
  }

  const assessorRole = assignment?.assessor_role
  const showHardSkill = assessorRole === 'ATASAN' || assessorRole === 'KOMITE'

  useEffect(() => {
    if (assessorRole && !showHardSkill && activeTab === 'hs') {
      setActiveTab('ss')
    }
    if (assessorRole && showHardSkill && activeTab === 'ss') {
      setActiveTab('hs')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessorRole])

  const tabs: { key: Tab; label: string }[] = [
    ...(showHardSkill ? [{ key: 'hs' as Tab, label: 'Hard Skill' }] : []),
    { key: 'ss', label: 'Soft Skill' },
    { key: 'akhlak', label: 'AKHLAK' },
  ]

  const isLoading = ssLoading || akhlakLoading || assignmentLoading || kpiLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">Memuat formulir...</div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Formulir Penilaian</h1>
        <p className="text-sm text-gray-500 mt-1">
          Isi penilaian untuk setiap aspek kompetensi
        </p>
      </div>

      {isClosed && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
          isAdminHr
            ? 'bg-amber-50 border border-amber-200 text-amber-800'
            : 'bg-gray-50 border border-gray-200 text-gray-600'
        }`}>
          Periode ini sudah <strong>Ditutup</strong>
          {isAdminHr
            ? ' — Anda dapat mengisi dan mengumpulkan penilaian atas nama penilai sebagai Admin/HR.'
            : ' — formulir tidak bisa diisi atau diubah.'}
        </div>
      )}
      {isDraft && (
        <div className="mb-4 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          Periode ini masih <strong>Draft</strong> — Anda bisa melihat formulir dan menyimpan draft, tapi belum bisa mengumpulkan penilaian sampai periode dibuka.
        </div>
      )}
      {isSubmitted && (
        <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          Penilaian ini sudah <strong>dikumpulkan</strong>
          {isAdminHr
            ? ' — Anda dapat mengubah jawaban dan komentar sebagai Admin/HR.'
            : ' — tidak bisa diubah lagi.'}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-6">
        {tabs.map((tab) => {
          const badge = tab.key === 'ss' ? ssMissing : tab.key === 'akhlak' ? akhlakMissing : 0
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {!isSubmitted && badge > 0 && (
                <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold rounded-full bg-red-500 text-white">
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Hard Skill Tab */}
      {activeTab === 'hs' && (
        <KpiHardSkillTab
          kpiAssignments={kpiAssignments ?? []}
          realisasiDraft={realisasiDraft}
          setRealisasiDraft={setRealisasiDraft}
          realisasiPctDraft={realisasiPctDraft}
          setRealisasiPctDraft={setRealisasiPctDraft}
          hsScores={hsScores}
          setHsScores={setHsScores}
          kpiNotes={kpiNotes}
          setKpiNotes={setKpiNotes}
          isSubmitted={isSubmitted}
          isClosed={isClosed}
          isAdminHr={isAdminHr}
          updateRealisasiMutation={updateRealisasiMutation}
        />
      )}

      {/* Soft Skill Tab */}
      {activeTab === 'ss' && (
        <div className="space-y-8">
          {(ssIndicators ?? []).map((indicator) => (
            <div key={indicator.id} className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-1">{indicator.name}</h3>
              {indicator.description && (
                <p className="text-sm text-gray-500 mb-4">{indicator.description}</p>
              )}

              <div className="space-y-4">
                {indicator.questions.map((q, i) => (
                  <div key={q.id} className="pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                    <p className="text-sm text-gray-700 mb-2">
                      {i + 1}. {q.question_text}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[4, 3, 2, 1].map((val) => (
                        <ScoreButton
                          key={val}
                          value={val}
                          label={SS_SCORE_LABELS[val]}
                          selected={ssScores[q.id] === val}
                          onClick={() => {
                            if (!isSubmitted || isAdminHr) setSsScores({ ...ssScores, [q.id]: val })
                          }}
                          color={SS_COLORS[val]}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Catatan (opsional — jika ada catatan, multiplier turun dari 1.1 menjadi 1.0)
                </label>
                <textarea
                  value={ssNotes[indicator.id] ?? ''}
                  onChange={(e) => setSsNotes({ ...ssNotes, [indicator.id]: e.target.value })}
                  disabled={isAdminHr ? false : (isSubmitted || isClosed)}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                  placeholder="Catatan untuk kompetensi ini..."
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AKHLAK Tab */}
      {activeTab === 'akhlak' && (
        <div className="space-y-8">
          {(akhlakValues ?? []).map((value) => (
            <div key={value.id} className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-1">{value.name}</h3>
              {value.description && (
                <p className="text-sm text-gray-500 mb-4">{value.description}</p>
              )}

              <div className="space-y-4">
                {value.behaviors.map((behavior, i) => (
                  <div
                    key={behavior.id}
                    className="pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                  >
                    <p className="text-sm text-gray-700 mb-2">
                      {i + 1}. {behavior.behavior_text}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {[4, 3, 2, 1].map((val) => (
                        <ScoreButton
                          key={val}
                          value={val}
                          label={AKHLAK_SCORE_LABELS[val]}
                          selected={akhlakScores[behavior.id] === val}
                          onClick={() => {
                            if (!isSubmitted || isAdminHr)
                              setAkhlakScores({ ...akhlakScores, [behavior.id]: val })
                          }}
                          color={SS_COLORS[val]}
                        />
                      ))}
                    </div>
                    <input
                      type="text"
                      value={akhlakNotes[behavior.id] ?? ''}
                      onChange={(e) =>
                        setAkhlakNotes({ ...akhlakNotes, [behavior.id]: e.target.value })
                      }
                      disabled={isAdminHr ? false : (isSubmitted || isClosed)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                      placeholder="Tindakan aktual (opsional)..."
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Score Summary */}
      <div className="mt-8 bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Ringkasan Skor</h3>
          <span className="text-xs text-gray-400">Diperbarui otomatis saat Anda mengisi form</span>
        </div>
        <div className="p-5">
          <div className={`grid gap-3 ${showHardSkill ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {/* HS — only for ATASAN / KOMITE */}
            {showHardSkill && (
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">
                  Hard Skill
                </p>
                <p className="text-xs text-blue-400 mb-3">Bobot 70%</p>
                <p className="text-2xl font-bold text-blue-700">
                  {hsScore !== null ? hsScore.toFixed(2) : '—'}
                </p>
                {hsQuestionAvg !== null && (
                  <p className="text-xs text-blue-400 mt-1">
                    Avg pertanyaan: {hsQuestionAvg.toFixed(1)} / 5
                  </p>
                )}
                {(kpiAssignments ?? []).some((k) => !k.realisasi_pct) &&
                  hsQuestionAvg !== null && (
                    <p className="text-xs text-orange-500 mt-1">⚠ Isi realisasi KPI</p>
                  )}
              </div>
            )}

            {/* SS */}
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">
                Soft Skill
              </p>
              <p className="text-xs text-green-400 mb-3">Bobot 20%</p>
              <p className="text-2xl font-bold text-green-700">
                {ssScore !== null ? ssScore.toFixed(4) : '—'}
              </p>
            </div>

            {/* AKHLAK */}
            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-1">
                AKHLAK
              </p>
              <p className="text-xs text-purple-400 mb-3">Bobot 10%</p>
              <p className="text-2xl font-bold text-purple-700">
                {akhlakScore !== null ? akhlakScore.toFixed(4) : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {(!isClosed || isAdminHr) && (
        <div className="mt-8 space-y-3">
          {/* Incomplete warning */}
          {!isSubmitted && (!ssComplete || !akhlakComplete) && (
            <div className="px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 space-y-1">
              <div className="flex items-center gap-2 font-medium">
                <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                Lengkapi semua penilaian sebelum mengumpulkan:
              </div>
              <ul className="ml-6 list-disc space-y-0.5 text-yellow-700">
                {ssMissing > 0 && (
                  <li>Soft Skill: <span className="font-semibold">{ssMissing} pertanyaan</span> belum dinilai (dari {allSsQuestions.length} total)</li>
                )}
                {akhlakMissing > 0 && (
                  <li>AKHLAK: <span className="font-semibold">{akhlakMissing} perilaku</span> belum dinilai (dari {allAkhlakBehaviors.length} total)</li>
                )}
              </ul>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            {isAdminHr ? (
              <>
                <button
                  onClick={() => submitMutation.mutate(buildPayload(true))}
                  disabled={submitMutation.isPending}
                  className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-40"
                >
                  {submitMutation.isPending && !confirmSubmit ? 'Menyimpan...' : 'Simpan Perubahan'}
                </button>
                {!isSubmitted && (
                  confirmSubmit ? (
                    <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                      <span className="text-sm text-orange-800">Kumpulkan atas nama penilai?</span>
                      <button
                        onClick={() => submitMutation.mutate(buildPayload(false))}
                        disabled={submitMutation.isPending}
                        className="px-3 py-1 bg-orange-600 text-white rounded text-xs font-medium hover:bg-orange-700 disabled:opacity-40"
                      >
                        {submitMutation.isPending ? 'Mengumpulkan...' : 'Ya, Kumpulkan'}
                      </button>
                      <button
                        onClick={() => setConfirmSubmit(false)}
                        className="px-3 py-1 border border-gray-300 text-gray-600 rounded text-xs hover:bg-gray-50"
                      >
                        Batal
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmSubmit(true)}
                      disabled={submitMutation.isPending || !ssComplete || !akhlakComplete}
                      title={!ssComplete ? 'Lengkapi Soft Skill terlebih dahulu' : !akhlakComplete ? 'Lengkapi AKHLAK terlebih dahulu' : undefined}
                      className="px-5 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Submit untuk Penilai
                    </button>
                  )
                )}
              </>
            ) : (
              !isSubmitted && (
                <>
                  <button
                    onClick={() => submitMutation.mutate(buildPayload(true))}
                    disabled={submitMutation.isPending || !canSaveDraft}
                    className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-40"
                  >
                    Simpan Draft
                  </button>
                  <button
                    onClick={() => submitMutation.mutate(buildPayload(false))}
                    disabled={submitMutation.isPending || !canFinalSubmit}
                    title={!canSubmit && !isSubmitted ? 'Periode belum dibuka' : !ssComplete ? 'Lengkapi Soft Skill terlebih dahulu' : !akhlakComplete ? 'Lengkapi AKHLAK terlebih dahulu' : undefined}
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitMutation.isPending ? 'Mengumpulkan...' : 'Kumpulkan Penilaian'}
                  </button>
                </>
              )
            )}
          </div>
        </div>
      )}
    </div>
  )
}
